import React, { useEffect, useState } from "react";
import { Form, Row, Input, Col, Card, Spin, Skeleton, Switch } from "antd";
import { useSelector } from "react-redux";
import passwordValidator from "password-validator";
import {
  SelectBox,
  InputBox,
  TextArea,
  isBrowser,
} from "../../../shared/custom-ant-d";
import { ZipRegex } from "../../../shared/helpers";

const schema = new passwordValidator();

export const PersonalInformationFEMA = (props) => {
  const Auth = useSelector((state) => state.Auth);
  const LookUpData = useSelector((state) => state.LookUpData);

  const [countryZipRegex, setCountryZipRegex] = useState(null);
  const [isPrimaryAddressSameAsMailing, setIsPrimaryAddressSameAsMailing] = useState(null);
  
  // Set default value for new applications and copy primary address if applicable
  useEffect(() => {
    // Use a timeout to ensure the form and component are fully initialized
    const timer = setTimeout(() => {
      const currentFormValues = props.form.getFieldsValue();
      const applicationID = currentFormValues.applicationID;
      
      if (!applicationID || applicationID === 0 || applicationID === "0") {
        // New application mode - default to No (false)
        setIsPrimaryAddressSameAsMailing(false);
        props.form.setFieldsValue({ isPrimaryAddressSameAsMailing: false });
      } else {
        // Resume mode - check if there's an existing value
        const existingValue = currentFormValues.isPrimaryAddressSameAsMailing;
        if (existingValue !== undefined) {
          setIsPrimaryAddressSameAsMailing(existingValue);
          
          // If resuming with toggle set to true, copy primary address values
          if (existingValue === true) {
            const primaryValues = props.form.getFieldsValue([
              'addressLine1', 'addressLine2', 'addressLine3', 
              'city', 'state', 'postal'
            ]);
            
            props.form.setFieldsValue({
              mailingAddress1: primaryValues.addressLine1,
              mailingAddress2: primaryValues.addressLine2,
              mailingAddress3: primaryValues.addressLine3,
              mailingCity: primaryValues.city,
              mailingStateID: primaryValues.state,
              mailingZipCode: primaryValues.postal
            });
          }
        }
      }
    }, 100); // Small delay to ensure form is ready
    
    return () => clearTimeout(timer);
  }, [props.form]);



  useEffect(() => {
    if (!props.hideIds && LookUpData !== null) {
      props.getLookupData();
    }
  }, [LookUpData]);

  useEffect(() => {
    if (props.countries && Auth.countryID) {
      getSelectedCountryVals(Auth.countryID);
    }
  }, [Auth.countryID, props.countries]);

  const handleCountryChange = (value, resetField = true) => {
    try {
      var currentCountry = props.countries.filter((item, i) => {
        return item.countryID === value;
      });

      props.setSelectedCountryName(currentCountry[0].countryName);
    } catch {
      console.log("No Countries to show.");
    }

    try {
      var statesToShow = props.statesList.filter((state, i) => {
        return state.countryID === value;
      });

      props.setStateOfCountry(statesToShow);

      if (resetField) {
        props.form.setFieldsValue({ state: "" });
      } else {
        props.form.setFieldsValue({ state: Auth.state });
      }

      getSelectedCountryVals(value);
      props.form.setFieldsValue({ postal: "" });
    } catch {
      props.setStateOfCountry([]);
    }
  };

  const getSelectedCountryVals = (value) => {
    var currentCountrySelected = props.countries.filter((item, i) => {
      return item.countryID === value;
    });

    props.phoneFormatReq(currentCountrySelected[0].countryCode);
    props.handleZipPostalRequirments(currentCountrySelected[0].countryCode);
    setCountryZipRegex(ZipRegex(currentCountrySelected[0].countryCode));
  };



  return (
    <>
      <Row justify="center">
        <Col sm={23}>
          <Card title="Personal Information" className="iqor-header">
            {props.isProfileLoading ? (
              <Skeleton active />
            ) : (
              <>
                <Row justify="center">
                  <Col md={11} xs={24}>
                    <Form.Item
                      label="Candidate ID"
                      name="candidateId"
                      hidden={props.hideIds ? true : false}
                    >
                      {isBrowser ? <Input disabled /> : <InputBox disabled />}
                    </Form.Item>
                    <Form.Item
                      label="Job Requisition ID"
                      name="JobReqID"
                      hidden={props.hideIds ? false : true}
                    >
                      {isBrowser ? <Input disabled /> : <InputBox disabled />}
                    </Form.Item>
                  </Col>
                  <Col md={{ span: 11, offset: 1 }} xs={24}>
                    <Form.Item
                      label="Email Address"
                      name="email"
                      rules={[
                        {
                          required: true,
                          message: "Please fill in your email!",
                        },
                        {
                          type: "email",
                          message: "Please input a valid email",
                        },
                      ]}
                    >
                      {isBrowser ? <Input disabled /> : <InputBox disabled />}
                    </Form.Item>
                  </Col>
                </Row>

                <Row justify="center">
                  <Col md={11} xs={24}>
                    <Form.Item
                      label="Legal First Name"
                      name="firstname"
                      rules={[
                        {
                          required: true,
                          message: "Please fill in your first name!",
                        },
                        {
                          max: 50,
                          message:
                            "First name cannot be longer than 50 characters",
                        },
                      ]}
                    >
                      {isBrowser ? <Input /> : <InputBox />}
                    </Form.Item>
                  </Col>

                  <Col md={{ span: 11, offset: 1 }} xs={24}>
                    <Form.Item 
                      label={
                        <span>
                          Middle Name{" "}
                          <span style={{ color: '#1890ff', fontSize: '12px', fontWeight: 'normal' }}>
                            (Enter your middle name or type 'NMN' if none)
                          </span>
                        </span>
                      } 
                      name="middlename"
                      rules={[
                        {
                          required: true,
                          message: "Please fill in your middle name!",
                        },
                        {
                          max: 50,
                          message: "Middle name cannot be longer than 50 characters",
                        },
                      ]}
                    >
                      {isBrowser ? <Input /> : <InputBox />}
                    </Form.Item>
                  </Col>
                </Row>

                <Row justify="center">
                  <Col md={11} xs={24}>
                    <Form.Item
                      label="Legal Last Name"
                      name="lastname"
                      rules={[
                        {
                          required: true,
                          message: "Please fill in your last name!",
                        },
                        {
                          max: 50,
                          message:
                            "Last name cannot be longer than 50 characters",
                        },
                      ]}
                    >
                      {isBrowser ? <Input /> : <InputBox />}
                    </Form.Item>
                  </Col>

                  <Col md={{ span: 11, offset: 1 }} xs={24}>
                    <Form.Item
                      label="Mobile/Primary Number"
                      name="phone"
                      rules={[
                        {
                          required: true,
                        },
                        {
                          len: props.phoneMinChar,
                          message:
                            "Mobile number must contain " +
                            props.phoneMinChar +
                            " digits.",
                        },
                        () => ({
                          validator(_, value) {
                            schema.is().not().letters().is().not().symbols();

                            if (
                              !schema
                                .validate(value, { list: true })
                                .includes("letters") &&
                              !schema
                                .validate(value, { list: true })
                                .includes("symbols")
                            ) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject(
                                "Mobile number must contain numbers only."
                              );
                            }
                          },
                        }),
                      ]}
                    >
                      {isBrowser ? <Input /> : <InputBox />}
                    </Form.Item>
                  </Col>
                </Row>

                <Row justify="center">
                  <Col md={11} xs={24}>
                    <Form.Item
                      label="Secondary Phone (optional)"
                      name="secondaryPhone"
                      rules={[
                        {
                          min: 7,
                          message:
                            "Secondary phone must have at least 7 digits.",
                        },
                        {
                          max: 10,
                          message:
                            "Secondary phone can only have max of 10 digits",
                        },
                        () => ({
                          validator(_, value) {
                            schema.is().not().letters().is().not().symbols();

                            if (
                              value === null ||
                              value === "" ||
                              (!schema
                                .validate(value, { list: true })
                                .includes("letters") &&
                                !schema
                                  .validate(value, { list: true })
                                  .includes("symbols"))
                            ) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject(
                                "Secondary phone must contain numbers only."
                              );
                            }
                          },
                        }),
                      ]}
                    >
                      {isBrowser ? <Input /> : <InputBox />}
                    </Form.Item>
                  </Col>

                  <Col md={{ span: 11, offset: 1 }} xs={24}>
                    <Form.Item
                      label="Preferred Contact Method"
                      name="preferedContactMethod"
                      rules={[{ required: true }]}
                    >
                      <SelectBox
                        data={
                          props.preferredContactMethodList &&
                          props.preferredContactMethodList.map((item, i) => {
                            return {
                              value: item.codeValueID,
                              label: item.codeValueDescription,
                            };
                          })
                        }
                        label={""}
                        onChange={(value) => {
                          props.form.setFieldsValue({
                            preferedContactMethod: value,
                          });
                        }}
                        onDismiss={(value) => {
                          props.form.setFieldsValue({
                            preferedContactMethod: value,
                          });
                        }}
                        showSearch={true}
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        notFoundContent={
                          !props.preferredContactMethodList ? (
                            <Spin size="small" />
                          ) : null
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row justify="center">
                  <Col md={11} xs={24}>
                    <Form.Item
                      name="countryId"
                      label="Country/Region of Residence"
                      hasFeedback
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <SelectBox
                        data={
                          props.countries &&
                          props.countries.map((item, i) => {
                            return {
                              value: item.countryID,
                              label: item.countryName,
                            };
                          })
                        }
                        label={""}
                        onChange={(value) => {
                          props.form.setFieldsValue({ countryId: value });
                          handleCountryChange(value);
                        }}
                        onDismiss={(value) => {
                          props.form.setFieldsValue({ countryId: value });
                        }}
                        showSearch={true}
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        disabled={props.hideIds ? true : false}
                        notFoundContent={
                          !props.countries ? <Spin size="small" /> : null
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col md={{ span: 11, offset: 1 }} xs={24}>
                    {/* Empty column to maintain 2-column layout */}
                  </Col>
                </Row>

                {/* Physical/Primary Address Subsection */}
                <Row justify="center" style={{ marginTop: '20px' }}>
                  <Col span={24}>
                    <div style={{ fontWeight: 'bold', marginBottom: '16px', fontSize: '16px' }}>
                      Physical/Primary Address
                    </div>
                  </Col>
                </Row>

                <Row justify="center">
                  <Col md={11} xs={24}>
                    <Form.Item
                      label="Address Line 1"
                      name="addressLine1"
                      rules={[
                        { required: true },
                        {
                          max: 255,
                          message:
                            "Address Line 1 cannot be longer than 255 characters",
                        },
                      ]}
                    >
                      <TextArea />
                    </Form.Item>
                  </Col>

                  <Col md={{ span: 11, offset: 1 }} xs={24}>
                    <Form.Item
                      label="Address Line 2"
                      name="addressLine2"
                      rules={[
                        {
                          max: 255,
                          message:
                            "Address Line 2 cannot be longer than 255 characters",
                        },
                      ]}
                    >
                      <TextArea />
                    </Form.Item>
                  </Col>
                </Row>

                <Row justify="center">
                  <Col md={11} xs={24}>
                    <Form.Item
                      label="Address Line 3"
                      name="addressLine3"
                      rules={[
                        {
                          max: 255,
                          message:
                            "Address Line 3 cannot be longer than 255 characters",
                        },
                      ]}
                    >
                      <TextArea />
                    </Form.Item>
                  </Col>

                  <Col md={{ span: 11, offset: 1 }} xs={24}>
                    <Form.Item
                      label="City"
                      name="city"
                      rules={[{ required: true }]}
                    >
                      {isBrowser ? <Input /> : <InputBox />}
                    </Form.Item>
                  </Col>
                </Row>

                <Row justify="center">
                  <Col md={11} xs={24}>
                    <Form.Item
                      label="State/Province"
                      name="state"
                      rules={[{ required: true }]}
                    >
                      <SelectBox
                        data={
                          props.statesOfCountry &&
                          props.statesOfCountry.map((item, i) => {
                            return {
                              value: item.stateID,
                              label: item.stateName,
                            };
                          })
                        }
                        label={""}
                        onChange={(value) => {
                          props.form.setFieldsValue({ state: value });
                        }}
                        onDismiss={(value) => {
                          props.form.setFieldsValue({ state: value });
                        }}
                        showSearch={true}
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        notFoundContent={
                          !props.statesOfCountry ? <Spin size="small" /> : null
                        }
                      />
                    </Form.Item>
                  </Col>

                  {props.zipRequirments.length !== undefined && (
                    <Col md={{ span: 11, offset: 1 }} xs={24}>
                      <Form.Item
                        label="Zip/Postal Code"
                        name="postal"
                        dependencies={["countryId"]}
                        rules={[
                          {
                            required: props.zipRequirments.required,
                          },
                          () => ({
                            validator(_, value) {
                              if (
                                !props.zipRequirments.required &&
                                value === ""
                              ) {
                                return Promise.resolve();
                              }

                              if (countryZipRegex !== null) {
                                if (!countryZipRegex.regex.test(value))
                                  return Promise.reject(countryZipRegex.msg);
                                else return Promise.resolve();
                              }
                            },
                          }),
                        ]}
                      >
                        {isBrowser ? <Input /> : <InputBox />}
                      </Form.Item>
                    </Col>
                  )}
                </Row>

                {/* Toggle Switch for Mailing Address */}
                <Row justify="center" style={{ marginTop: '16px', marginBottom: '16px' }}>
                  <Col md={23} xs={24}>
                    {/* Hidden Form.Item for form state management */}
                    <Form.Item
                      name="isPrimaryAddressSameAsMailing"
                      valuePropName="checked"
                      initialValue={isPrimaryAddressSameAsMailing}
                      style={{ display: 'none' }}
                    >
                      <Switch />
                    </Form.Item>
                    
                    {/* Custom layout for label and switch on same line */}
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                          Is Mailing Address same as Physical/Primary Address?
                        </span>
                        <Switch
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                          checked={isPrimaryAddressSameAsMailing}
                          onChange={(checked) => {
                            setIsPrimaryAddressSameAsMailing(checked);
                            props.form.setFieldsValue({ isPrimaryAddressSameAsMailing: checked });
                            
                            if (checked) {
                              // Copy primary address values to mailing address fields
                              const primaryValues = props.form.getFieldsValue([
                                'addressLine1', 'addressLine2', 'addressLine3', 
                                'city', 'state', 'postal'
                              ]);
                              
                              props.form.setFieldsValue({
                                mailingAddress1: primaryValues.addressLine1,
                                mailingAddress2: primaryValues.addressLine2,
                                mailingAddress3: primaryValues.addressLine3,
                                mailingCity: primaryValues.city,
                                mailingStateID: primaryValues.state,
                                mailingZipCode: primaryValues.postal
                              });
                            }
                          }}
                        />
                      </div>
                      
                      {/* Blue text on separate line */}
                      <div style={{ paddingLeft: '0px' }}>
                        <span style={{ color: '#1890ff', fontSize: '12px', lineHeight: '1.4' }}>
                          (Mailing address will be used for iQor equipment shipping if eligible)
                        </span>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Mailing Address Subsection */}
                <Row justify="center" style={{ marginTop: '20px' }}>
                  <Col span={24}>
                    <div style={{ fontWeight: 'bold', marginBottom: '16px', fontSize: '16px' }}>
                      Mailing Address
                    </div>
                  </Col>
                </Row>

                <Row justify="center">
                  <Col md={11} xs={24}>
                    <Form.Item
                      label="Mailing Address Line 1"
                      name="mailingAddress1"
                      rules={[
                        { required: isPrimaryAddressSameAsMailing === false, message: "Mailing Address Line 1 is required" },
                        {
                          max: 255,
                          message: "Address Line 1 cannot be longer than 255 characters",
                        },
                      ]}
                    >
                      <Input.TextArea disabled={isPrimaryAddressSameAsMailing === true} />
                    </Form.Item>
                  </Col>

                  <Col md={{ span: 11, offset: 1 }} xs={24}>
                    <Form.Item
                        label="Mailing Address Line 2"
                      name="mailingAddress2"
                      rules={[
                        {
                          max: 255,
                          message: "Address Line 2 cannot be longer than 255 characters",
                        },
                      ]}
                    >
                      <Input.TextArea disabled={isPrimaryAddressSameAsMailing === true} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row justify="center">
                  <Col md={11} xs={24}>
                    <Form.Item
                        label="Mailing Address Line 3"
                      name="mailingAddress3"
                      rules={[
                        {
                          max: 255,
                          message: "Address Line 3 cannot be longer than 255 characters",
                        },
                      ]}
                    >
                      <Input.TextArea disabled={isPrimaryAddressSameAsMailing === true} />
                    </Form.Item>
                  </Col>

                  <Col md={{ span: 11, offset: 1 }} xs={24}>
                    <Form.Item
                        label="Mailing City"
                      name="mailingCity"
                      rules={[{ required: isPrimaryAddressSameAsMailing === false }]}
                    >
                      {isBrowser ? 
                        <Input disabled={isPrimaryAddressSameAsMailing === true} /> : 
                        <InputBox disabled={isPrimaryAddressSameAsMailing === true} />
                      }
                    </Form.Item>
                  </Col>
                </Row>

                <Row justify="center">
                  <Col md={11} xs={24}>
                    <Form.Item
                      label="State/Province"
                      name="mailingStateID"
                      rules={[{ required: isPrimaryAddressSameAsMailing === false }]}
                    >
                      <SelectBox
                        data={
                          props.statesOfCountry &&
                          props.statesOfCountry.map((item, i) => {
                            return {
                              value: item.stateID,
                              label: item.stateName,
                            };
                          })
                        }
                        label={""}
                        onChange={(value) => {
                          props.form.setFieldsValue({ mailingStateID: value });
                        }}
                        onDismiss={(value) => {
                          props.form.setFieldsValue({ mailingStateID: value });
                        }}
                        showSearch={true}
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        disabled={isPrimaryAddressSameAsMailing === true}
                        notFoundContent={
                          !props.statesOfCountry ? <Spin size="small" /> : null
                        }
                      />
                    </Form.Item>
                  </Col>

                  {props.zipRequirments.length !== undefined && (
                    <Col md={{ span: 11, offset: 1 }} xs={24}>
                      <Form.Item
                          label="Mailing Zip/Postal Code"
                        name="mailingZipCode"
                        dependencies={["countryId"]}
                        rules={[
                          {
                            required: isPrimaryAddressSameAsMailing === false ? props.zipRequirments.required : false,
                          },
                          () => ({
                            validator(_, value) {
                              if (isPrimaryAddressSameAsMailing === true) {
                                return Promise.resolve();
                              }
                              
                              if (
                                !props.zipRequirments.required &&
                                value === ""
                              ) {
                                return Promise.resolve();
                              }

                              if (countryZipRegex !== null) {
                                if (!countryZipRegex.regex.test(value))
                                  return Promise.reject(countryZipRegex.msg);
                                else return Promise.resolve();
                              }
                            },
                          }),
                        ]}
                      >
                        {isBrowser ? 
                          <Input disabled={isPrimaryAddressSameAsMailing === true} /> : 
                          <InputBox disabled={isPrimaryAddressSameAsMailing === true} />
                        }
                      </Form.Item>
                    </Col>
                  )}
                </Row>

                <Row justify="center">
                  <Col
                    md={
                      props.zipRequirments.length !== undefined
                        ? { span: 11 }
                        : { span: 11 }
                    }
                    xs={24}
                  >
                    {props.showEducLvl ? (
                      <Form.Item
                        label="What is your highest level of education?"
                        name="highestLevelOfEducationID"
                        rules={[{ required: props.hideIds }]}
                        //initialValue={Auth.stateID}
                      >
                        <SelectBox
                          data={
                            LookUpData.levelOfEducationList &&
                            LookUpData.levelOfEducationList.map((item, i) => {
                              return {
                                value: item.codeValueID,
                                label: item.expText,
                              };
                            })
                          }
                          label={""}
                          onChange={(value) => {
                            props.form.setFieldsValue({
                              highestLevelOfEducationID: value,
                            });
                          }}
                          onDismiss={(value) => {
                            props.form.setFieldsValue({
                              highestLevelOfEducationID: value,
                            });
                          }}
                          showSearch={true}
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          notFoundContent={
                            LookUpData.levelOfEducationList !== undefined &&
                            LookUpData.levelOfEducationList !== null &&
                            LookUpData.levelOfEducationList.length > 0 ? (
                              <Spin size="small" />
                            ) : null
                          }
                        />
                      </Form.Item>
                    ) : (
                      <></>
                    )}
                  </Col>

                  {props.zipRequirments.length === undefined ? (
                    <>
                      <Col xs={0} md={{ span: 11, offset: 1 }}></Col>
                    </>
                  ) : (
                    <></>
                  )}
                </Row>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
};
